import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFlow } from 'context/FlowContext';
import CompactMealCard from 'components/shared/CompactMealCard';
import MealModal from 'components/shared/MealModal';
import HouseholdMemberTabs from 'components/storefront/HouseholdMemberTabs';
import AddMemberModal from 'components/storefront/AddMemberModal';
import ScrollArrows from 'components/shared/ScrollArrows';
import { LANDING_ASSETS } from 'constants/storefrontAssets';
import { fetchMenu, sendQuizInvite } from 'services/publicFlowService';
import { useScrollArrows } from 'hooks/useScrollArrows';
import { useRestrictionsPrefill } from 'hooks/useRestrictionsPrefill';
import { useSubscriptionGate } from 'hooks/useSubscriptionGate';
import {
  scoreMenuForMember,
  restrictionSummary,
  memberIngredientConflicts,
} from 'utils/memberRestrictions';
import { allergenConflictLabel, conflictIngredientNames } from 'utils/allergenLabels';
import { quizNutritionForMealType } from 'utils/nutritionGoals';
import {
  groupDishesBySection,
  formatMenuWeekTitle,
  mealTypeChoices,
  withFamilyPlatters,
} from 'utils/menuSections';
import { MENU_PAGE_COPY } from 'constants/menuContent';
import { replaceSingleSelect, toggleMultiSelect } from 'utils/ingredientOptions';
import { sortQuantityFirst } from 'utils/orderSorting';
import {
  PRIMARY_MEMBER_DEFAULT_NAME,
  cartLineId,
  createDefaultMember,
  memberDisplayName,
} from 'utils/household';
import {
  buildCartLine,
  clearAdjustmentHistory,
  duplicateCartLine,
  lineIngredients,
  relineForMember,
} from 'utils/cartLine';
import ConfirmModal from 'components/storefront/ConfirmModal';

const dishKey = (dishId, mealType) => `${dishId}-${mealType}`;
// A dish on the menu as both a breakfast and a main gets a card in each section,
// so the portion picked on one is tracked per section rather than per dish.
const choiceKey = (sectionId, dishId) => `${sectionId}::${dishId}`;
const ADDED_TOAST_DURATION_MS = 4000;

/** Which calorie number a dish's card should show for this member, in priority
 *  order: a Skip Portioning dish's own fixed calories always win (set
 *  server-side regardless of login/quiz state); otherwise, a member who has
 *  taken the quiz and isn't nutrition-suppressed sees their real per-meal goal
 *  from the quiz results (quizNutritionForMealType); everyone else (no quiz
 *  yet, hidden, or genuinely logged out) sees the 600/150 default the backend
 *  already sent. */
function calorieInfoForMember(dish, member) {
  if (dish?.calorieInfo?.source === 'dish') return dish.calorieInfo;
  return quizNutritionForMealType(dish?.mealType, member) ?? dish?.calorieInfo ?? null;
}

/** Merge a catalog dish with its matching cart line (if any) into the shape
 *  CompactMealCard / MealModal / substitution sections expect.
 *
 *  `preview` is the buildCartLine result for a dish with no cart line yet, so the
 *  card shows the customer's adjusted version rather than the menu's recipe. It
 *  stays quantity 0 — nothing is in the cart until they add it.
 *
 *  The allergy warning is derived here rather than read off a stored flag: it
 *  answers "do the ingredients selected right now clash with the person this meal
 *  is for?", and both halves of that keep changing — an ingredient swapped in the
 *  modal, the meal reassigned to someone else, a restriction added to a member.
 *  Deriving it means every one of those is picked up with nothing to keep in
 *  sync; `member` is whoever the line belongs to, not necessarily the active tab. */
function toMealView(dish, cartLine, preview, member) {
  const line = cartLine || preview;
  const allergenConflicts = line
    ? memberIngredientConflicts(lineIngredients(line), member)
    : [];
  const base = {
    dishId: dish.dishId ?? cartLine?.dishId,
    imageUrl: dish.imageUrl ?? cartLine?.imageUrl,
    price: dish.price ?? cartLine?.price,
    meal: dish.mealType ?? cartLine?.mealType,
    isTemporary: false,
    // Dish/catalog-level, not cart-line-level — always read off the menu
    // catalog entry regardless of whether this meal is already in the cart.
    // See calorieInfoForMember for the quiz-taken vs default priority.
    calorieInfo: calorieInfoForMember(dish, member),
  };
  if (cartLine) {
    return {
      ...base,
      recordId: cartLine.lineId,
      actualIndex: cartLine.lineId,
      itemName: cartLine.name,
      quantity: cartLine.quantity,
      originalIngredients: cartLine.originalIngredients || [],
      finalIngredients: cartLine.finalIngredients || [],
      proteinOptions: cartLine.proteinOptions || [],
      veggieOptions: cartLine.veggieOptions || [],
      starchOptions: cartLine.starchOptions || [],
      sauceOptions: cartLine.sauceOptions || [],
      garnishOptions: cartLine.garnishOptions || [],
      autoSubstitutions: cartLine.autoSubstitutions || [],
      allergyAdjusted: cartLine.allergyAdjusted || false,
      hasAllergenConflict: allergenConflicts.length > 0,
      allergenConflicts,
      selectionResolved: true,
      totalOriginalVeggie: cartLine.totalOriginalVeggie || 0,
    };
  }
  return {
    ...base,
    recordId: cartLineId(dish.__memberId, dish.dishId, dish.mealType),
    actualIndex: cartLineId(dish.__memberId, dish.dishId, dish.mealType),
    itemName: dish.name,
    quantity: 0,
    originalIngredients: dish.originalIngredients || [],
    finalIngredients: preview?.finalIngredients || dish.originalIngredients || [],
    proteinOptions: preview?.proteinOptions || dish.proteinOptions || [],
    veggieOptions: preview?.veggieOptions || dish.veggieOptions || [],
    starchOptions: preview?.starchOptions || dish.starchOptions || [],
    sauceOptions: preview?.sauceOptions || dish.sauceOptions || [],
    garnishOptions: preview?.garnishOptions || dish.garnishOptions || [],
    autoSubstitutions: [],
    allergyAdjusted: preview?.allergyAdjusted || false,
    hasAllergenConflict: allergenConflicts.length > 0,
    allergenConflicts,
    selectionResolved: Boolean(preview),
    totalOriginalVeggie: dish.totalOriginalVeggie || 0,
  };
}

export default function MenuPage() {
  const {
    activeMember,
    activeMemberId,
    householdMembers,
    setActiveMember,
    addHouseholdMember,
    removeHouseholdMember,
    updateHouseholdMember,
    entryFlow,
    startMenuFlow,
    addCartLine,
    setCartLineQuantity,
    resetCartForWeek,
    setCartWeek,
    cartWeek,
    cart,
    updateCartLine,
    moveCartLineToMember,
    removeFromCart,
  } = useFlow();

  useRestrictionsPrefill(householdMembers, updateHouseholdMember);
  const subscription = useSubscriptionGate();
  const [orderingAnyway, setOrderingAnyway] = useState(false);

  const [allDishes, setAllDishes] = useState([]);
  const [deliveryWeek, setDeliveryWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addedNotice, setAddedNotice] = useState(null);
  // Editing a serving that's already in the cart — changes apply to it live.
  const [modalLineId, setModalLineId] = useState(null);
  // A serving being configured but NOT yet in the cart: the card's Customize
  // button builds this, the modal edits it, and only "Add to Cart" commits it
  // (Claire, Aug 2026). Nothing reaches the cart before that click.
  const [draft, setDraft] = useState(null);
  // Portion choice per dish card, before it's added to the cart. Keyed by
  // section+dishId; defaults to the cheapest variant in the card's own section.
  const [mealTypeChoice, setMealTypeChoice] = useState({});
  // Which section's card opened the modal, so a portion picked in there lands on
  // that card and not on the dish's card in another section.
  const [modalSectionId, setModalSectionId] = useState(null);
  const [menuChangedNotice, setMenuChangedNotice] = useState(false);
  // "+ Add someone new" from the modal's recipient picker, rather than the
  // household tabs — same AddMemberModal, opened from a second spot.
  const [addRecipientModalOpen, setAddRecipientModalOpen] = useState(false);
  // A recipient switch waiting on the customer: the ingredients they picked
  // conflict with the person they're moving the meal to. Nothing is re-adjusted —
  // they just acknowledge it. { memberId, member, conflicts }
  const [pendingRecipient, setPendingRecipient] = useState(null);
  const modalRef = useRef(null);

  const { shouldShowArrows, getScrollState, scrollLeft, scrollRight, handleScroll } = useScrollArrows();

  const activeMemberLabel = memberDisplayName(activeMember, PRIMARY_MEMBER_DEFAULT_NAME);

  useEffect(() => {
    document.title = "This Week's Menu — Square Fare";
    if (!entryFlow) startMenuFlow();
    setLoading(true);
    fetchMenu({ personalized: Boolean(activeMember?.hasFullQuiz) })
      .then((data) => {
        const dishes = withFamilyPlatters(data.dishes || []);
        setAllDishes(dishes);
        const week = data.deliveryWeek || null;
        setDeliveryWeek(week);
        // The menu rotates weekly — a cart built from last week's menu points at
        // dishes that are no longer cookable, so drop it and tell the customer.
        if (week && cartWeek && cartWeek !== week) {
          resetCartForWeek(week);
          setMenuChangedNotice(true);
        } else {
          if (week && !cartWeek) setCartWeek(week);
          // Same week, but a specific dish fell off the menu (e.g. sold out) —
          // silently drop just that cart line. An empty menu means the fetch
          // failed, not that every dish vanished, so skip it there.
          if (dishes.length) {
            const availableKeys = new Set(dishes.map((d) => dishKey(d.dishId, d.mealType)));
            cart.forEach((line) => {
              if (!availableKeys.has(dishKey(line.dishId, line.mealType))) {
                removeFromCart(line.lineId);
              }
            });
          }
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryFlow, startMenuFlow, activeMember?.hasFullQuiz]);

  const dishes = useMemo(
    () => scoreMenuForMember(allDishes, activeMember),
    [allDishes, activeMember],
  );

  const sections = useMemo(() => groupDishesBySection(dishes), [dishes]);

  const dishByKey = useMemo(() => {
    const map = {};
    dishes.forEach((dish) => { map[dishKey(dish.dishId, dish.mealType)] = dish; });
    return map;
  }, [dishes]);

  /** Dish entries carrying portion options, by dishId — the modal only has a
   *  line, not the card's dish, so it looks its portions up here. The options are
   *  menu-wide, so it doesn't matter which section's entry wins. */
  const consolidatedByDishId = useMemo(() => {
    const map = {};
    sections.forEach((section) => section.dishes.forEach((dish) => {
      if (dish.variantsByMealType) map[String(dish.dishId)] = dish;
    }));
    return map;
  }, [sections]);

  /** The variant (the per-meal-type dish record) currently chosen for a card.
   *  With nothing picked yet that's the card's own section default — never
   *  mealTypeOptions[0], which is now Breakfast for a dish sold as all three. */
  const resolveVariant = useCallback((sectionId, dish, cartLine) => {
    if (!dish.variantsByMealType) return dish;
    const chosen = cartLine?.mealType
      || mealTypeChoice[choiceKey(sectionId, dish.dishId)]
      || dish.mealType;
    return dish.variantsByMealType[chosen] || dish;
  }, [mealTypeChoice]);

  // Each cart line is one independently-customizable serving (quantity always 1) —
  // clicking Add on an already-ordered dish adds another card rather than bumping a
  // count, mirroring MealPlanViewer. A dish with no cart lines yet renders as a
  // single "available" card; a dish with N cart lines renders N ordered cards.
  const sortedSections = useMemo(
    () =>
      sections.map((section) => {
        const cards = section.dishes.flatMap((dish) => {
          // A card matches cart lines for any meal type ITS section covers, so an
          // added Dinner shows on the Lunch & Dinner card. Scoped to the section
          // rather than to every portion the dish offers: a dish listed under two
          // sections would otherwise show the one serving as ordered under both.
          const dishMealTypes = section.mealTypes.length
            ? section.mealTypes
            : [dish.mealType];
          const matchingLines = cart.filter(
            (c) => c.memberId === activeMemberId
              && String(c.dishId) === String(dish.dishId)
              && dishMealTypes.includes(c.mealType),
          );
          if (matchingLines.length === 0) return [{ dish, cartLine: null }];
          return matchingLines.map((cartLine) => ({ dish, cartLine }));
        }).map((card) => {
          const variant = resolveVariant(section.id, card.dish, card.cartLine);
          // Adjust as the menu renders, not on Customize. Seeded off the lineId, so
          // this and the draft built later pick the same swaps. A line already in
          // the cart keeps whatever the customer set.
          const preview = card.cartLine
            ? null
            : buildCartLine(variant, activeMemberId, activeMember);
          // Built once here so the card renders and sorts off the same view —
          // including its derived allergy warning. Cards live under a member's
          // tab, so the meal is always for the active member.
          const meal = toMealView(
            { ...variant, __memberId: activeMemberId },
            card.cartLine,
            preview,
            activeMember,
          );
          return { ...card, variant, preview, meal };
        });
        return {
          ...section,
          cards: sortQuantityFirst(
            cards,
            (card) => (card.cartLine ? 1 : 0),
            (card) => card.meal.hasAllergenConflict,
          ),
        };
      }),
    [sections, cart, activeMemberId, activeMember, resolveVariant],
  );

  /** A card only lists servings whose portion belongs to its own section, so once
   *  a portion from another section is committed the serving is listed there and
   *  this card drops back to its default instead of showing a portion twice. */
  const settleMealTypeChoice = useCallback((sectionId, dishId, mealType) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section || section.mealTypes.includes(mealType)) return;
    setMealTypeChoice((prev) => {
      const key = choiceKey(sectionId, dishId);
      if (!(key in prev)) return prev;
      const { [key]: _dropped, ...rest } = prev;
      return rest;
    });
  }, [sections]);

  /** Switch a card to another portion (Breakfast/Lunch/Dinner/Snack). If it's
   *  already in the cart, the line is re-pointed at that variant so its price
   *  updates too — and it moves to the section that portion belongs to. */
  const onMealTypeChange = (sectionId, dish, cartLine, nextMealType) => {
    setMealTypeChoice((prev) => ({ ...prev, [choiceKey(sectionId, dish.dishId)]: nextMealType }));
    if (!cartLine) return;
    const variant = dish.variantsByMealType?.[nextMealType];
    if (!variant) return;
    updateCartLine(cartLine.lineId, {
      mealType: nextMealType,
      price: variant.price,
      lineId: cartLineId(cartLine.memberId, dish.dishId, nextMealType),
    });
    settleMealTypeChoice(sectionId, dish.dishId, nextMealType);
  };

  /** Opens the customization modal. A serving that's already in the cart is
   *  edited in place; anything else opens as a draft, so a dish only enters the
   *  cart when the customer confirms with "Add to Cart". The draft is the line the
   *  card was already previewing, so the modal opens on what the card showed. */
  const openCustomizeModal = (lineId, cartLine, preview, sectionId = null) => {
    setModalSectionId(sectionId);
    if (cartLine) {
      setModalLineId(lineId);
      return;
    }
    if (preview) {
      setDraft(preview);
      return;
    }
    const [memberId, dishId, mealType] = lineId.split('::');
    const dish = dishByKey[dishKey(dishId, mealType)];
    if (!dish) return;
    setDraft(buildCartLine(dish, memberId, householdMembers.find((m) => m.id === memberId)));
  };

  const closeModal = () => {
    setModalLineId(null);
    setDraft(null);
    setModalSectionId(null);
    setPendingRecipient(null);
  };

  const handleAddHouseholdMember = (data) => {
    const id = addHouseholdMember(data);
    // Adding someone with an email puts them on the quiz-invite list so their
    // meals get tailored too (Claire, Aug 2026).
    if (data?.email) {
      sendQuizInvite({
        email: data.email,
        name: data.name,
        invitedBy: householdMembers[0]?.name
          && householdMembers[0].name !== PRIMARY_MEMBER_DEFAULT_NAME
          ? householdMembers[0].name
          : '',
      });
    }
    return id;
  };

  const onQuantityChange = (lineId, newQty) => {
    const existing = cart.find((c) => c.lineId === lineId);
    if (newQty <= 0) {
      if (existing) removeFromCart(lineId);
      return;
    }
    // Adding another serving of a dish already in the cart drafts a copy of it
    // rather than bumping this one's quantity, so the second serving can be
    // customized on its own — mirroring MealPlanViewer. Identical duplicates
    // (same dish/portion/ingredients) are only consolidated for display in the
    // cart/checkout view — see cartSignature.js.
    if (existing) {
      if (newQty > (existing.quantity || 1)) setDraft(duplicateCartLine(existing));
      else setCartLineQuantity(lineId, newQty);
      return;
    }
    openCustomizeModal(lineId, null);
  };

  const withSubstitution = (category, mutate) => (lineId, ...args) => {
    // Substitutions on a draft stay local until it's added to the cart. The
    // allergy warning follows the new selection on its own — it's derived in
    // toMealView, not stored — so swapping the offending ingredient out clears it.
    if (draft && lineId === draft.lineId) {
      const { updatedOptions, updatedFinalIngredients } = mutate(draft, ...args);
      setDraft({ ...draft, [category]: updatedOptions, finalIngredients: updatedFinalIngredients });
      return;
    }
    const cartLine = cart.find((c) => c.lineId === lineId);
    if (!cartLine) return;
    const { updatedOptions, updatedFinalIngredients } = mutate(cartLine, ...args);
    updateCartLine(lineId, {
      [category]: updatedOptions,
      finalIngredients: updatedFinalIngredients,
    });
  };

  const onProteinSubstitution = withSubstitution('proteinOptions', (cartLine, newId, oldId) =>
    replaceSingleSelect(cartLine.proteinOptions, cartLine.finalIngredients, newId, oldId));
  const onStarchSubstitution = withSubstitution('starchOptions', (cartLine, newId, oldId) =>
    replaceSingleSelect(cartLine.starchOptions, cartLine.finalIngredients, newId, oldId));
  const onSauceSubstitution = withSubstitution('sauceOptions', (cartLine, newId, oldId) =>
    replaceSingleSelect(cartLine.sauceOptions, cartLine.finalIngredients, newId, oldId));
  const onVeggieSubstitution = withSubstitution('veggieOptions', (cartLine, targetId, isActive) =>
    toggleMultiSelect(cartLine.veggieOptions, cartLine.finalIngredients, targetId, isActive));
  const onToggleGarnish = withSubstitution('garnishOptions', (cartLine, targetId, isActive) =>
    toggleMultiSelect(cartLine.garnishOptions, cartLine.finalIngredients, targetId, isActive));

  const renderCard = ({ dish, cartLine, variant, preview, meal }, section) => {
    const lineId = cartLine
      ? cartLine.lineId
      : cartLineId(activeMemberId, dish.dishId, variant.mealType);
    return (
      <CompactMealCard
        key={lineId}
        meal={meal}
        actualIndex={lineId}
        onOpenModal={() => openCustomizeModal(lineId, cartLine, preview, section.id)}
        onQuantityChange={onQuantityChange}
        isLocked={false}
        saving={false}
        addLabel={cartLine ? 'Add Another' : 'Customize'}
        showPrice={!section.priceNote}
        mealTypeChoices={mealTypeChoices(dish)}
        selectedMealType={variant.mealType}
        onMealTypeChange={(next) => onMealTypeChange(section.id, dish, cartLine, next)}
      />
    );
  };

  // The line the modal is showing: either the uncommitted draft or a real cart
  // line. Both carry the same shape, so the modal doesn't care which it is.
  const modalLine = useMemo(
    () => draft || cart.find((c) => c.lineId === modalLineId) || null,
    [draft, cart, modalLineId],
  );

  /** Whoever the modal's meal is currently for — after a reassignment that's no
   *  longer the member whose tab is open. */
  const modalMember = useMemo(
    () => householdMembers.find((m) => m.id === modalLine?.memberId) || null,
    [householdMembers, modalLine],
  );

  const modalMeal = useMemo(
    () => (modalLine
      ? toMealView({ ...modalLine, __memberId: modalLine.memberId }, modalLine, null, modalMember)
      : null),
    [modalLine, modalMember],
  );

  const modalDish = modalLine ? consolidatedByDishId[String(modalLine.dishId)] : null;
  const modalMealTypeChoices = useMemo(() => mealTypeChoices(modalDish), [modalDish]);

  /** Another portion from inside the modal. A draft is rebuilt from the other
   *  variant — they're separate dish records with their own ingredient options —
   *  while a line already in the cart keeps its customizations and is only
   *  re-pointed, same as the card's picker. */
  const onModalMealTypeChange = (nextMealType) => {
    if (!modalLine || modalLine.mealType === nextMealType) return;
    const variant = modalDish?.variantsByMealType?.[nextMealType];
    if (!variant) return;
    setMealTypeChoice((prev) => ({
      ...prev,
      [choiceKey(modalSectionId, modalLine.dishId)]: nextMealType,
    }));

    if (draft) {
      const member = householdMembers.find((m) => m.id === draft.memberId);
      setDraft(buildCartLine(variant, draft.memberId, member));
      return;
    }

    // The meal type is part of the lineId, so this re-keys the line — and takes a
    // duplicate's id when that portion is already in the cart, so both survive.
    const relinedId = cartLineId(modalLine.memberId, modalLine.dishId, nextMealType);
    const nextLineId = cart.some((c) => c.lineId === relinedId)
      ? duplicateCartLine({ ...modalLine, lineId: relinedId }).lineId
      : relinedId;
    updateCartLine(modalLine.lineId, {
      mealType: nextMealType,
      price: variant.price,
      lineId: nextLineId,
    });
    setModalLineId(nextLineId);
    settleMealTypeChoice(modalSectionId, modalLine.dishId, nextMealType);
  };

  /** Moves the meal to another member. The ingredients are left exactly as the
   *  customer chose them — reassigning never re-runs the auto-substitution — and
   *  the allergy warning re-derives itself for the new owner. */
  const applyRecipientChange = (memberId) => {
    setPendingRecipient(null);
    // The member id is part of the lineId, so reassigning a meal means re-keying
    // the line rather than editing this one in place.
    if (draft) {
      setDraft(clearAdjustmentHistory(relineForMember(draft, memberId)));
      return;
    }
    const nextLineId = moveCartLineToMember(modalLine.lineId, memberId);
    if (nextLineId) setModalLineId(nextLineId);
  };

  /** A meal built for one person can carry ingredients the next person is
   *  allergic to. We don't adjust anything on their behalf here — the customer is
   *  told what conflicts and confirms. */
  const requestRecipientChange = (memberId, member) => {
    if (!modalLine || memberId === modalLine.memberId) return;
    const conflicts = memberIngredientConflicts(lineIngredients(modalLine), member);
    if (conflicts.length === 0) {
      applyRecipientChange(memberId);
      return;
    }
    setPendingRecipient({ memberId, member, conflicts });
  };

  /** "…conflict with Sam's Dairy." A free-text exclusion has no restriction group
   *  behind it, and allergenConflictLabel then echoes the ingredient names — which
   *  would read as "Feta conflicts with Sam's Feta" — so that case says
   *  "allergies" instead. */
  const conflictRestrictionText = (conflicts) => {
    const label = allergenConflictLabel(conflicts);
    return label && label !== conflictIngredientNames(conflicts) ? label : 'allergies';
  };

  if (subscription.checking) {
    return <p className="sf-menu-loading">Loading…</p>;
  }

  if (subscription.hasSubscription && !orderingAnyway) {
    return (
      <main className="sf-menu-main">
        <div className="sf-menu-body">
          <p className="sf-menu-restriction-note" role="status">
            You already have an active subscription, so to change your own meals please use
            your meal plan.{' '}
            <a href={`/meal-plan?customer=${subscription.token}`}>Go to my meal plan</a>
            <br />
            Ordering for someone else?{' '}
            <a href="#order-anyway" onClick={(e) => { e.preventDefault(); setOrderingAnyway(true); }}>
              Continue to the menu
            </a>
          </p>
        </div>
      </main>
    );
  }

  return (
    <>
      {subscription.hasSubscription && (
        <p className="sf-menu-restriction-note" role="status">
          You can edit your own meals any time in your subscription.{' '}
          <a href={`/meal-plan?customer=${subscription.token}`}>Go to my meal plan</a>
        </p>
      )}
      {/* ── Production-style hero header ── */}
      <header
        className="sf-menu-hero"
        style={{ backgroundImage: `url(${LANDING_ASSETS.headerBg})` }}
      >
        <div className="sf-menu-hero__overlay" aria-hidden="true" />
        <div className="sf-menu-hero__inner">
          <h1 className="sf-menu-hero__title">Square Fare<br />Personalized Meal Plan</h1>
          {activeMember?.email && (
            <p className="sf-menu-hero__sub">{activeMember.email}</p>
          )}
        </div>
      </header>

      <main className="sf-menu-main">
        {/* ── Week + delivery info strip ── */}
        <div className="sf-menu-week-strip">
          <p className="sf-menu-week-strip__week">{formatMenuWeekTitle(deliveryWeek)}</p>
          <p className="sf-menu-week-strip__info">{MENU_PAGE_COPY.deliveryHome}</p>
          <p className="sf-menu-week-strip__info">{MENU_PAGE_COPY.serviceArea}</p>
          <div className="sf-menu-week-strip__deadlines">
            {MENU_PAGE_COPY.orderDeadlines.map((line) => (
              <span key={line} className="sf-menu-week-strip__deadline-pill">
                🕐 {line}
              </span>
            ))}
          </div>
        </div>

        <div className="sf-menu-body sf-menu-tabs">
          {/* ── Household member tabs ── */}
          <HouseholdMemberTabs
            members={householdMembers}
            activeMemberId={activeMemberId}
            onSelect={setActiveMember}
            onAddMember={handleAddHouseholdMember}
            onRemoveMember={removeHouseholdMember}
            onUpdateMember={updateHouseholdMember}
          />

          <div className="sf-menu-tabframe">

          {menuChangedNotice && (
            <p className="sf-menu-restriction-note" role="status">
              This week&apos;s menu has changed since you last visited, so your cart was
              cleared. Please choose from the dishes below.
            </p>
          )}

          {/* ── Restriction / quiz nudge ── */}
          {restrictionSummary(activeMember) !== 'No restrictions set' ? (
            <p className="sf-menu-restriction-note">
              Showing meals for <strong>{activeMemberLabel}</strong>. Ingredients that
              conflict with <strong>{restrictionSummary(activeMember)}</strong> have already
              been swapped out below — the few meals we couldn&apos;t adjust are marked and
              listed last.
            </p>
          ) : !activeMember?.hasFullQuiz && entryFlow === 'menu-first' ? (
            <p className="sf-menu-restriction-note">
              Browse and add meals à la carte.{' '}
              <Link to="/quiz">Take the quiz</Link> to filter by your nutrition profile.
            </p>
          ) : null}

          {/* ── Dish sections ── */}
          {loading ? (
            <p className="sf-menu-loading">Loading menu…</p>
          ) : dishes.length === 0 ? (
            <p className="sf-menu-loading">
              No meals available this week.
            </p>
          ) : (
            sortedSections.map((section) => {
              const scrollState = getScrollState(section.id);
              return (
                <section key={section.id} className="sf-menu-section" aria-labelledby={`section-${section.id}`}>
                  <h2 id={`section-${section.id}`} className="sf-menu-section-title">
                    {section.label}
                    {section.priceNote && (
                      <span className="sf-menu-section-price">{section.priceNote}</span>
                    )}
                  </h2>
                  <div className="sf-menu-scroll-wrap">
                    <div
                      className="sf-menu-scroll-row"
                      data-meal-type={section.id}
                      onScroll={(e) => handleScroll(section.id, e)}
                    >
                      {section.cards.map((card) => renderCard(card, section))}
                    </div>
                    <ScrollArrows
                      mealType={section.id}
                      shouldShowArrows={shouldShowArrows}
                      itemCount={section.cards.length}
                      scrollState={scrollState}
                      onScrollLeft={scrollLeft}
                      onScrollRight={scrollRight}
                    />
                  </div>
                </section>
              );
            })
          )}
          </div>
        </div>
      </main>

      {addedNotice && (
        <div className="sf-toast" role="status" style={{ animationDuration: `${ADDED_TOAST_DURATION_MS}ms` }}>
          {addedNotice}
        </div>
      )}

      {modalMeal && (
        <>
          <MealModal
            meal={modalMeal}
            onClose={closeModal}
            modalRef={modalRef}
            saving={false}
            isLocked={false}
            // Auto-substitution badges belong to the pass where the dish is first
            // being configured — reopening an added meal shouldn't re-show them.
            showAddedBanner={Boolean(draft)}
            useALaCartePricing
            isDraft={Boolean(draft)}
            addToCartLabel={draft ? 'Add to Cart' : 'Save'}
            mealTypeChoices={modalMealTypeChoices}
            selectedMealType={modalLine.mealType}
            onMealTypeChange={onModalMealTypeChange}
            recipients={householdMembers}
            recipientId={modalLine.memberId}
            onRecipientChange={(memberId) => requestRecipientChange(
              memberId,
              householdMembers.find((m) => m.id === memberId),
            )}
            onAddRecipient={() => setAddRecipientModalOpen(true)}
            onAddToCart={() => {
              // Editing an existing serving (customizations save as you click) —
              // just confirm. A draft is what actually enters the cart here.
              if (draft) {
                addCartLine(draft);
                settleMealTypeChoice(modalSectionId, draft.dishId, draft.mealType);
                setAddedNotice(`${draft.name} has been added to your cart.`);
                setTimeout(() => setAddedNotice(null), ADDED_TOAST_DURATION_MS);
              }
              closeModal();
            }}
            onProteinSubstitution={onProteinSubstitution}
            onVeggieSubstitution={onVeggieSubstitution}
            onStarchSubstitution={onStarchSubstitution}
            onSauceSubstitution={onSauceSubstitution}
            onToggleGarnish={onToggleGarnish}
          />
          <AddMemberModal
            open={addRecipientModalOpen}
            className="sf-modal--over-meal-modal"
            onClose={() => setAddRecipientModalOpen(false)}
            onSave={(data) => {
              const id = handleAddHouseholdMember(data);
              setAddRecipientModalOpen(false);
              // The new member isn't in householdMembers until the next render,
              // so the allergy check reads the restrictions off the form payload.
              // Declining the confirmation leaves them in the household — adding
              // someone is its own action — and just doesn't move the meal.
              requestRecipientChange(id, createDefaultMember({ ...data, id }));
            }}
          />
          {pendingRecipient && (
            <ConfirmModal
              className="sf-modal--over-meal-modal"
              tone="warn"
              icon="⚠"
              title={`Allergy conflict for ${memberDisplayName(pendingRecipient.member)}`}
              cancelLabel="Cancel"
              confirmLabel="Move anyway"
              onCancel={() => setPendingRecipient(null)}
              onConfirm={() => applyRecipientChange(pendingRecipient.memberId)}
            >
              Selected ingredients <strong>{conflictIngredientNames(pendingRecipient.conflicts)}</strong>
              {' '}conflict with {memberDisplayName(pendingRecipient.member)}&apos;s{' '}
              <strong>{conflictRestrictionText(pendingRecipient.conflicts)}</strong>.
            Please make necessary changes before saving.
            </ConfirmModal>
          )}
        </>
      )}

      {/* Footer removed: FlowLayout now renders the shared SiteFooter, and this
          page was showing StorefrontFooter underneath it — two footers. */}
    </>
  );
}
